import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import Modal from "../../../components/base/Modal";
import { buildThemeClasses } from "../theme";
import EntityForm from "./EntityForm";
import { PayrollViewToggle } from "./PayrollViewToggle";

// ---------------------------------------------------------------------------
// Shell CRUD reutilizable, respaldado por API, para la mini-app Nómina.
// Adaptado de Layouts/release/components/ApiCrudSection.tsx (que a su vez sale
// de Layouts/mantenimiento), con el toggle grid/list (PayrollViewToggle): cada
// tabla ofrece vista de tabla (list) y vista de tarjetas (grid). El padre es
// dueño de las filas y de los handlers async create/update/delete, así que el
// mismo shell sirve para el catálogo, el historial y los check lists.
// Data-agnóstico, responsive y con dark mode.
// ---------------------------------------------------------------------------

export type Row = Record<string, unknown> & { id: string };

export interface Column {
  key: string;
  label: string;
  align?: "left" | "center" | "right";
  width?: string;
  render?: (row: Row) => React.ReactNode;
}

export interface RepeatColumn {
  key: string;
  label: string;
  type?: "text" | "number" | "select" | "checkbox";
  placeholder?: string;
  /** Opciones cuando `type = select`. */
  options?: { value: string; label: string }[];
}

export interface FormField {
  key: string;
  label: string;
  type?: "text" | "number" | "textarea" | "select" | "date" | "repeat" | "checkbox";
  required?: boolean;
  placeholder?: string;
  options?: { value: string; label: string }[];
  defaultValue?: string | number | boolean;
  /** Texto de ayuda bajo el campo. */
  hint?: string;
  /** Columnas de un campo `repeat` (líneas), p. ej. reglas de consumo. */
  repeatColumns?: RepeatColumn[];
  /** Etiqueta del botón "añadir fila" de un campo `repeat`. */
  addRowLabel?: string;
}

interface ApiCrudSectionProps {
  title: string;
  subtitle?: string;
  icon?: string;
  columns: Column[];
  fields: FormField[];
  rows: Row[];
  loading?: boolean;
  error?: string | null;
  searchKey?: string;
  readOnly?: boolean;
  addLabel?: string;
  /** Contenido libre entre el encabezado y la tabla (filtros, KPIs…). */
  banner?: React.ReactNode;
  /** Acciones de la barra, a la izquierda del botón "Agregar". */
  toolbar?: React.ReactNode;
  /** Acciones extra por fila, antes de editar/eliminar. */
  extraActions?: (row: Row) => React.ReactNode;
  /** Tarjeta propia para la vista grid; por defecto se arma desde `columns`. */
  gridCard?: (row: Row) => React.ReactNode;
  onCreate?: (values: Record<string, string>) => Promise<void> | void;
  onUpdate?: (id: string, values: Record<string, string>) => Promise<void> | void;
  onDelete?: (id: string) => Promise<void> | void;
}

export default function ApiCrudSection({
  title,
  subtitle,
  icon,
  columns,
  fields,
  rows,
  loading = false,
  error = null,
  searchKey,
  readOnly = false,
  addLabel,
  banner,
  toolbar,
  extraActions,
  gridCard,
  onCreate,
  onUpdate,
  onDelete,
}: ApiCrudSectionProps) {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);

  const [query, setQuery] = useState("");
  const [selectedView, setSelectedView] = useState<"grid" | "list">("list");
  const [modal, setModal] = useState<null | { mode: "add" | "edit"; row?: Row }>(null);
  const [page, setPage] = useState(0);
  const pageSize = 10;

  const key = searchKey ?? columns[0]?.key;
  const filtered = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.toLowerCase();
    return rows.filter((r) => String(r[key] ?? "").toLowerCase().includes(q));
  }, [rows, query, key]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = filtered.slice(safePage * pageSize, safePage * pageSize + pageSize);

  const closeModal = () => setModal(null);

  const handleSubmit = async (values: Record<string, string>) => {
    if (modal?.mode === "edit" && modal.row) {
      await onUpdate?.(modal.row.id, values);
    } else {
      await onCreate?.(values);
    }
    closeModal();
  };

  const cellAlign = (a?: string) =>
    a === "right" ? "text-right" : a === "center" ? "text-center" : "text-left";

  const hasRowActions = !readOnly || !!extraActions;

  const rowActions = (row: Row) =>
    hasRowActions ? (
      <div className="flex items-center justify-center gap-1">
        {extraActions?.(row)}
        {!readOnly && onUpdate && (
          <button
            title={t("payroll.edit")}
            onClick={() => setModal({ mode: "edit", row })}
            className="rounded-md p-1.5 text-primary-500 hover:bg-primary-500/10"
          >
            <i className="ri-edit-line" />
          </button>
        )}
        {!readOnly && onDelete && (
          <button
            title={t("payroll.delete")}
            onClick={() => onDelete(row.id)}
            className="rounded-md p-1.5 text-red-500 hover:bg-red-500/10"
          >
            <i className="ri-delete-bin-line" />
          </button>
        )}
      </div>
    ) : null;

  // Tarjeta por defecto de la vista grid, armada desde las columnas.
  const defaultCard = (row: Row) => (
    <div className={`rounded-xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
      <div className="mb-2 flex items-start justify-between gap-2">
        <span className={`font-semibold ${tc.text}`}>
          {columns[0]?.render ? columns[0].render(row) : String(row[columns[0]?.key] ?? "")}
        </span>
        {rowActions(row)}
      </div>
      <dl className="space-y-1">
        {columns.slice(1).map((c) => (
          <div key={c.key} className="flex justify-between gap-2 text-xs">
            <dt className={tc.textSecondary}>{c.label}</dt>
            <dd className={`text-right ${tc.text}`}>
              {c.render ? c.render(row) : String(row[c.key] ?? "")}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-6xl">
        {/* Encabezado */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {icon && (
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
                <i className={`${icon} text-xl`} />
              </span>
            )}
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>{title}</h1>
              {subtitle && <p className={`text-sm ${tc.textSecondary}`}>{subtitle}</p>}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {toolbar}
            <PayrollViewToggle
              themeClasses={tc}
              selectedView={selectedView}
              onViewChange={setSelectedView}
            />
            <div
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${tc.border} ${tc.cardBg}`}
            >
              <i className={`ri-search-line ${tc.textSecondary}`} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("payroll.search")}
                className={`w-28 bg-transparent text-sm outline-none sm:w-auto ${tc.text}`}
              />
            </div>
            {!readOnly && onCreate && (
              <button
                onClick={() => setModal({ mode: "add" })}
                className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-400"
              >
                <i className="ri-add-line" />
                <span className="whitespace-nowrap">{addLabel ?? t("payroll.add")}</span>
              </button>
            )}
          </div>
        </div>

        {banner}

        {error && (
          <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-500">
            <i className="ri-error-warning-line mr-1" />
            {error}
          </div>
        )}

        {/* Estados vacío/cargando compartidos por ambas vistas */}
        {loading ? (
          <div
            className={`rounded-2xl border p-8 text-center ${tc.cardBorder} ${tc.cardBg} ${tc.textSecondary}`}
          >
            <i className="ri-loader-4-line animate-spin mr-1" />
            {t("payroll.loading")}
          </div>
        ) : pageRows.length === 0 ? (
          <div
            className={`rounded-2xl border p-8 text-center ${tc.cardBorder} ${tc.cardBg} ${tc.textSecondary}`}
          >
            {t("payroll.empty")}
          </div>
        ) : selectedView === "grid" ? (
          /* --- Vista GRID (tarjetas) --- */
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {pageRows.map((row) => (
              <div key={row.id}>{(gridCard ?? defaultCard)(row)}</div>
            ))}
          </div>
        ) : (
          /* --- Vista LIST (tabla) --- */
          <div className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
            <div className="w-full overflow-x-auto">
              <table className="w-full overflow-hidden rounded-xl text-xs shadow-sm">
                <thead className="bg-primary-500 text-white">
                  <tr>
                    {columns.map((c) => (
                      <th
                        key={c.key}
                        style={{ width: c.width }}
                        className={`p-3 font-bold ${cellAlign(c.align)}`}
                      >
                        {c.label}
                      </th>
                    ))}
                    {hasRowActions && (
                      <th className="p-3 text-center font-bold">{t("payroll.actions")}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row, i) => (
                    <tr
                      key={row.id}
                      className={
                        isDarkMode
                          ? i % 2 === 0
                            ? "bg-[#232323] text-white"
                            : "bg-[#1C1C1C] text-white"
                          : i % 2 === 0
                          ? "bg-gray-50 text-gray-900"
                          : "bg-white text-gray-900"
                      }
                    >
                      {columns.map((c) => (
                        <td key={c.key} className={`p-2 ${cellAlign(c.align)}`}>
                          {c.render ? c.render(row) : String(row[c.key] ?? "")}
                        </td>
                      ))}
                      {hasRowActions && <td className="p-2">{rowActions(row)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {filtered.length > pageSize && (
          <div className="mt-3 flex items-center justify-center gap-3 text-sm">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={safePage === 0}
              className="rounded-lg px-3 py-1 disabled:opacity-40 hover:bg-primary-500/10"
            >
              <i className="ri-arrow-left-s-line" />
            </button>
            <span className={isDarkMode ? "text-gray-300" : "text-gray-700"}>
              {safePage + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={safePage >= totalPages - 1}
              className="rounded-lg px-3 py-1 disabled:opacity-40 hover:bg-primary-500/10"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        )}
      </div>

      {modal && (
        <Modal
          title={
            modal.mode === "edit"
              ? `${t("payroll.edit")} — ${title}`
              : `${t("payroll.new")} — ${title}`
          }
          onClose={closeModal}
          themeClasses={tc}
          width="max-w-3xl"
          content={
            // El wrapper da un ancla estable para consultar dentro del modal
            // (la tabla queda montada detrás y repite etiquetas de columna).
            <div data-testid="payroll-modal">
              <EntityForm
                fields={fields}
                initial={modal.row}
                themeClasses={tc}
                isDarkMode={isDarkMode}
                submitLabel={modal.mode === "edit" ? t("payroll.save") : t("payroll.create")}
                onCancel={closeModal}
                onSubmit={handleSubmit}
              />
            </div>
          }
        />
      )}
    </div>
  );
}
